import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { SiteLayout } from '../../components/SiteLayout';
import { Icon } from '../../components/Icon';
import { SkeletonCards } from '../../components/ui';
import { VideoCard } from '../../components/VideoCard';
import { DiscoveryError, TREND_CHIPS, asUrl } from '../../components/Discovery';
import { usePageMeta } from '../../lib/meta';
import { api, buildPath, type SearchResult, type Video } from '../../lib/api';
import { CATALOG } from '../../lib/plans';
import { useSession } from '../../session';

export const TOOLS = [
  { key: 'video', icon: 'play', title: 'Video', text: 'Save allowed media at source quality', href: '/download', tone: 'violet' },
  { key: 'playlist', icon: 'playlist', title: 'Playlist', text: 'Group saves into collections', href: '/library', tone: 'blue' },
  { key: 'channel', icon: 'channel', title: 'Channel', text: 'Search videos by creator', href: '/discover?focus=1', tone: 'teal' },
  { key: 'subtitles', icon: 'cc', title: 'Subtitles', text: 'Transcribe & translate', href: '/app/ai', tone: 'green' },
  { key: 'ai', icon: 'wave', title: 'AI Discover', text: 'Find similar videos with AI', href: '/discover?ask=1', tone: 'rose' },
];

export function HeroSearch({ id = 'home-q' }: { id?: string }) {
  const { route } = useLocation();
  const [q, setQ] = useState('');
  const submit = (e: Event) => {
    e.preventDefault();
    const v = q.trim();
    if (!v) return;
    const u = asUrl(v);
    route(u ? `/download?url=${encodeURIComponent(u)}` : buildPath('/discover', { q: v }));
  };
  return (
    <form class="hero-search" role="search" onSubmit={submit}>
      <Icon name="search" size={20} />
      <label for={id} class="sr-only">Search YouTube videos, channels, or paste a link</label>
      <input id={id} type="search" placeholder="Search YouTube videos, channels, or paste a link…" value={q} onInput={(e) => setQ(e.currentTarget.value)} maxLength={2048} autoComplete="off" />
      <button class="search-go" type="submit" aria-label="Search"><Icon name="search" size={20} /></button>
    </form>
  );
}

function seeAllHref(i: number) {
  const c = TREND_CHIPS[i]!;
  if (c.q) return buildPath('/discover', { q: c.q, order: 'viewCount', published: 'month' });
  return buildPath('/discover', { category: c.category });
}

export function Home() {
  usePageMeta('Ayonix Studio', 'Find amazing videos — works with YouTube — save media you’re allowed to download at its real quality, and discover similar content with AI.');
  const { me } = useSession();
  const [chip, setChip] = useState(0);
  const [trend, setTrend] = useState<Video[] | null>(null);
  const [trendErr, setTrendErr] = useState<unknown>(null);
  const [seed, setSeed] = useState<string | null>(null);
  const [similar, setSimilar] = useState<Video[] | null>(null);
  const [simErr, setSimErr] = useState<unknown>(null);

  const loadTrend = (i: number) => {
    const c = TREND_CHIPS[i]!;
    setTrend(null); setTrendErr(null);
    const req = c.q
      ? api<SearchResult>('/v1/discover/search', { query: { q: c.q, order: 'viewCount', published: 'month' } })
      : api<{ videos: Video[] }>('/v1/discover/trending', { query: { category: c.category } });
    req.then((r) => {
      setTrend(r.videos);
      if (!seed && r.videos[0]) setSeed(r.videos[0].id);
    }).catch(setTrendErr);
  };
  useEffect(() => loadTrend(chip), [chip]);
  useEffect(() => {
    if (!seed) return;
    api<{ results: { video: Video }[] }>(`/v1/discover/similar/${encodeURIComponent(seed)}`)
      .then((r) => setSimilar(r.results.map((x) => x.video))).catch(setSimErr);
  }, [seed]);

  return (
    <SiteLayout>
      <div class="container home">
        <section class="hero-card" aria-labelledby="home-h">
          <img class="hero-photo" src="/img/hero-lake.webp" srcSet="/img/hero-lake-960.webp 960w, /img/hero-lake.webp 1920w" sizes="(max-width: 900px) 100vw, 1240px" alt="" fetchpriority="high" />
          <div class="hero-shade" />
          <div class="hero-content">
            <h1 id="home-h" class="display"><span>Find Amazing Videos</span> <span class="grad">Discover &amp; Save</span></h1>
            <p class="lede">Search millions of videos, save media you’re allowed to download at its real quality, and find similar content with AI.</p>
          </div>
          <div class="hero-search-wrap"><HeroSearch /></div>
          <a class="photo-credit" href="/credits">Photo: Michal Klajban · CC BY-SA 4.0</a>
        </section>
        {me === null && <p class="caption muted quota-note">Signed out you get {CATALOG.anonymous_discovery_per_day} discovery requests a day. <a href="/signup">Create a free account</a> for more.</p>}

        <nav class="tool-tiles" aria-label="Tools">
          {TOOLS.map((t) => (
            <a key={t.key} class="tool-tile" href={t.href} data-testid={`tool-${t.key}`}>
              <span class={`tile-icon ${t.tone}`}><Icon name={t.icon} size={22} /></span>
              <strong>{t.title}</strong>
              <span>{t.text}</span>
            </a>
          ))}
        </nav>

        <section class="shelf" aria-labelledby="trend-h">
          <div class="shelf-head">
            <h2 id="trend-h">Trending Videos</h2>
            <a class="see-all" href={seeAllHref(chip)}>See All <Icon name="chevron" size={16} /></a>
          </div>
          <div class="chips scroll" role="group" aria-label="Trending topic">
            {TREND_CHIPS.map((c, i) => <button key={c.label} type="button" class="chip" aria-pressed={chip === i} onClick={() => setChip(i)}>{c.label}</button>)}
          </div>
          {trendErr ? <DiscoveryError err={trendErr} onRetry={() => loadTrend(chip)} />
            : !trend ? <SkeletonCards label="Loading trending videos" />
            : trend.length === 0 ? <p class="muted">No videos found for this topic right now.</p>
            : <div class="video-row" data-testid="trending">{trend.slice(0, 4).map((v) => <VideoCard key={v.id} v={v} />)}</div>}
        </section>

        {!trendErr && (seed || !trend) && (
          <section class="shelf" aria-labelledby="similar-h">
            <div class="shelf-head">
              <h2 id="similar-h">Similar Videos</h2>
              {seed && <a class="see-all" href={`/discover/v/${seed}`}>See All <Icon name="chevron" size={16} /></a>}
            </div>
            {simErr ? <DiscoveryError err={simErr} compact />
              : !similar ? <SkeletonCards label="Loading similar videos" />
              : similar.length === 0 ? <p class="muted">No similar videos found.</p>
              : <div class="video-row" data-testid="similar">{similar.slice(0, 4).map((v) => <VideoCard key={v.id} v={v} />)}</div>}
          </section>
        )}
      </div>
    </SiteLayout>
  );
}
